/**
 * Configurable data table.
 *
 * Columns shape:
 *   { label, key, align?: "left"|"right", render?: (row) => ReactNode, colorFn?: (row) => string }
 *
 * Props:
 *   columns      : Column[]
 *   rows         : object[]
 *   highlightFn  : (row) => boolean   — row gets subtle accent background when true
 *   emptyLabel   : string             — shown as a single muted row when rows is empty
 *
 * Style matches CLAUDE.md: 11px uppercase muted headers, 12px body, 1px #f0f0f0 row borders,
 * right-align numeric columns, tabular numerals everywhere.
 */
export function DataTable({ columns, rows, highlightFn, emptyLabel = "No data for this period" }) {
  return (
    <div className="overflow-x-auto">
      <table
        className="w-full text-[12px] border-collapse"
        style={{ fontFeatureSettings: "'tnum'" }}
      >
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`text-[11px] uppercase text-text-muted font-medium px-4 py-2.5 border-b border-border-light ${
                  col.align === "right" ? "text-right" : "text-left"
                }`}
                style={{ letterSpacing: "0.5px" }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(rows?.length ?? 0) === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-6 text-center text-text-muted border-b border-border-light"
              >
                {emptyLabel}
              </td>
            </tr>
          )}
          {rows.map((row, i) => {
            const highlighted = highlightFn?.(row);
            return (
              <tr
                key={row.id ?? i}
                className={
                  highlighted
                    ? "bg-accent-bg"
                    : "hover:bg-[var(--color-border-light)]/40"
                }
              >
                {columns.map((col) => {
                  const value = col.render ? col.render(row) : row[col.key];
                  const color = col.colorFn?.(row);
                  return (
                    <td
                      key={col.key}
                      className={`px-4 py-2.5 text-text-secondary border-b border-border-light ${
                        col.align === "right" ? "text-right" : "text-left"
                      }`}
                      style={color ? { color } : undefined}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
